/**
 * micStreamHolder.ts
 *
 * Module-level cache for the microphone MediaStream once the user has
 * granted access. Held for the lifetime of the page (released only on
 * tab close) so subsequent calls to "Call" / "Video Call" don't
 * re-prompt for permission.
 *
 * The Phase 4 call-surface model expects the user's first click on
 * Call or Video Call to BE the user-gesture for getUserMedia. After
 * that grant succeeds we stash the stream here and re-use it for the
 * lifetime of the tab.
 *
 * Why hold the stream open instead of stopping its tracks between
 * calls?
 *   - Permission UX: stopping all tracks releases the mic indicator
 *     but means the next call has to redo getUserMedia. On Safari the
 *     redo can prompt again under some configurations.
 *   - Latency: keeping the mic warm avoids the ~50-300ms
 *     getUserMedia round trip on every "call" press, making the
 *     phone-call metaphor feel snappier.
 *   - Trade-off: the browser shows a persistent mic indicator, which
 *     is desirable here (the user knows the avatar can hear if they
 *     start a call). It's NOT recording anything when no call is
 *     active — voice activity is only routed through the WebRTC peer
 *     connection during a call.
 *
 * Public API:
 *   - getOrAcquireMicStream() → returns the cached stream, or prompts
 *     and caches one if not yet granted. Throws on user denial.
 *   - getCachedMicStream() → returns the cached stream or null without
 *     prompting. For "should the Call button render?" checks.
 *   - releaseMicStream() → stops all tracks and clears the cache. Use
 *     on tab unload or when the user explicitly revokes.
 */

let cached: MediaStream | null = null
let inflight: Promise<MediaStream> | null = null

/**
 * Returns the cached mic stream or prompts the user. Must be called
 * from a user gesture handler the first time (browser security).
 *
 * Subsequent calls return the cached stream synchronously.
 *
 * Dispatches `avatar:mic-acquired` on the first successful grant so
 * useAvatarHealth can clear any audio-access issues and the action
 * bar can transition out of "ask for mic" state.
 */
export async function getOrAcquireMicStream(): Promise<MediaStream> {
  if (cached && cached.active) {
    return cached
  }
  if (inflight) {
    return inflight
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('navigator.mediaDevices.getUserMedia is not available')
  }
  inflight = navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => {
      cached = stream
      inflight = null
      if (typeof window !== 'undefined') {
        try {
          ;(window as unknown as { camlinSharedAudioStream?: MediaStream | null }).camlinSharedAudioStream = stream
        } catch {
          // Best-effort cross-cache sync.
        }
        window.dispatchEvent(new CustomEvent('avatar:mic-acquired'))
      }
      return stream
    })
    .catch((err) => {
      inflight = null
      throw err
    })
  return inflight
}

/**
 * Returns the cached mic stream without prompting. Returns null if no
 * grant has happened yet. Used by readiness checks that don't want to
 * trigger a permission prompt.
 */
export function getCachedMicStream(): MediaStream | null {
  if (cached && cached.active) {
    return cached
  }
  return null
}

/**
 * Stops all tracks and clears the cache. The user will need to grant
 * permission again on the next Call/Video Call click. Generally only
 * called on tab unload — most consumers want the stream to persist.
 */
export function releaseMicStream(): void {
  if (cached) {
    cached.getTracks().forEach((track) => track.stop())
    cached = null
  }
}

/**
 * Whether mic permission has been granted in this tab session. Pure
 * lookup — no side effects.
 */
export function isMicGranted(): boolean {
  return Boolean(cached && cached.active)
}

/**
 * Enable / disable every audio track on the shared mic stream(s). This is
 * the actual "mute the user's microphone" primitive: a disabled track
 * keeps the stream (and the browser's mic indicator) alive but produces
 * silence, so BOTH consumers of the shared stream go quiet:
 *
 *   - the WebRTC/SIP sender (JsSIP clones its local track from this
 *     stream — belt-and-braces alongside `session.mute()`), and
 *   - the browser-STT path (`sttFromMic` reuses
 *     `window.camlinSharedAudioStream`), so live sessions without a SIP
 *     destination stop transcribing while muted.
 *
 * We toggle both the module-level cache AND `window.camlinSharedAudioStream`
 * because `sttFromMic` can populate the window global on its own (without
 * going through getOrAcquireMicStream) and the two can diverge.
 */
export function setMicStreamEnabled(enabled: boolean): void {
  const streams = new Set<MediaStream>()
  if (cached) {
    streams.add(cached)
  }
  if (typeof window !== 'undefined') {
    const shared = (window as unknown as { camlinSharedAudioStream?: MediaStream | null })
      .camlinSharedAudioStream
    if (shared) {
      streams.add(shared)
    }
  }
  streams.forEach((stream) => {
    stream.getAudioTracks().forEach((track) => {
      track.enabled = enabled
    })
  })
}

/**
 * Plays a 1-sample silent buffer through both an HTMLAudioElement and a
 * Web Audio AudioContext to satisfy Chrome / Safari / iOS autoplay
 * policy in the same tick as the user gesture that called us.
 *
 * Why this exists: Phase 4 surfaces (AvatarCallSurface) suppress the
 * legacy LiveControlDock "Listen" button which used to be the gesture
 * that unlocked playback. Without that path, the SDK's first attempt
 * to play the bot's TTS audio (via SIP/RTP or HTTP /talk) is blocked
 * by autoplay policy and the user hears nothing.
 *
 * MUST be called synchronously from inside a user gesture handler
 * (click, pointerdown, keydown). setTimeout / promise.then break the
 * gesture chain and the unlock will silently no-op.
 *
 * Idempotent — safe to call multiple times. Does nothing in non-DOM
 * environments (SSR / tests).
 */
export function unlockAudioPlaybackSync(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  try {
    const AudioContextCtor =
      (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (AudioContextCtor) {
      const ctx = new AudioContextCtor()
      const buffer = ctx.createBuffer(1, 1, 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      source.start(0)
      if (ctx.state === 'suspended') {
        void ctx.resume().catch(() => {})
      }
    }
  } catch {
    // Best-effort — never let an unlock failure break the call start.
  }

  try {
    const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement | null
    if (remoteAudio) {
      remoteAudio.muted = false
      remoteAudio.volume = 1.0
      const playPromise = remoteAudio.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Expected on the very first call when no source is set yet.
          // The element is now primed and the next .play() (after the
          // SIP track lands) will succeed under the autoplay policy.
        })
      }
    }
  } catch {
    // Best-effort.
  }
}
